import { Component } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Iter38 — Page-level error boundary. Catches any runtime React error on
 * the wrapped subtree and shows a friendly fallback instead of a blank
 * white screen (which previously required F5 to recover).
 *
 * Usage: wrap each route in <ErrorBoundary>. Tracks the error in console
 * so dev tools still show the stack, but the user sees a recoverable UI.
 */
export class ErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="max-w-2xl mx-auto mt-16 p-6" data-testid="error-boundary-fallback">
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-6 space-y-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-700 mt-1 flex-shrink-0" />
            <div className="space-y-1">
              <h2 className="serif text-2xl font-semibold text-amber-900">Ups, dogodila se greška</h2>
              <p className="text-sm text-amber-800">
                Stranica je naišla na neočekivanu pogrešku. Pokušaj ponovno učitati ili se vrati na početnu.
              </p>
            </div>
          </div>
          {this.state.error?.message && (
            <details className="text-xs text-amber-800 bg-amber-100 rounded p-2 border border-amber-200">
              <summary className="cursor-pointer font-medium">Tehnički detalji</summary>
              <pre className="mt-2 whitespace-pre-wrap break-words">{this.state.error.message}</pre>
            </details>
          )}
          <div className="flex gap-2">
            <Button onClick={this.reset} variant="default" size="sm" data-testid="error-boundary-retry">
              <RefreshCw className="w-4 h-4 mr-2" /> Pokušaj ponovno
            </Button>
            <Button onClick={() => { window.location.href = "/"; }} variant="outline" size="sm" data-testid="error-boundary-home">
              Početna
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
